import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";
import api, { formatApiErrorDetail, getFileUrl } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { LANDING_ADMIN } from "@/constants/testIds";

// Fix BA (2026-10-30): fotos de la portada (hasta 6, en orden, cada una con su encuadre).
const MAX_HERO_IMAGES = 6;
const HERO_POSITIONS = [
  { value: "center", label: "Encuadre: centro" },
  { value: "left", label: "Encuadre: izquierda" },
  { value: "right", label: "Encuadre: derecha" },
];

/** Card de Ajustes: contenido editorial de la landing page publica (raiz del sitio). */
export default function LandingContentSettingsCard() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api.get("/landing/settings").then(({ data }) => setForm({
      ...data,
      // Fix BA: si todavia no hay lista de fotos, la foto de portada que ya estaba cargada pasa a ser la primera.
      hero_images: data.hero_images?.length ? data.hero_images : data.hero_image_url ? [{ url: data.hero_image_url, position: "center" }] : [],
    }));
  }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const heroImages = form?.hero_images || [];
  const setHeroImages = (fn) => setForm((f) => ({ ...f, hero_images: fn(f.hero_images || []) }));
  const moveHeroImage = (idx, dir) => setHeroImages((list) => {
    const next = [...list];
    [next[idx], next[idx + dir]] = [next[idx + dir], next[idx]];
    return next;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/landing/settings", {
        hero_title: form.hero_title || null,
        hero_subtitle: form.hero_subtitle || null,
        hero_quote: form.hero_quote || null,
        hero_images: heroImages,
        intro_title: form.intro_title || null,
        intro_text: form.intro_text || null,
        business_hours: form.business_hours || null,
        show_results_gallery: form.show_results_gallery,
        meta_title: form.meta_title || null,
        meta_description: form.meta_description || null,
      });
      setForm(data);
      toast({ title: "Contenido de la landing actualizado" });
    } catch (err) {
      toast({ title: "Error al guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5" data-testid={LANDING_ADMIN.contentCard}>
      <div>
        <h2 className="font-serif text-xl text-foreground">Landing page pública</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Contenido de la página de inicio (strani.com), la primera impresión de tu clínica online.
        </p>
      </div>

      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Fotos de la portada</Label>
        <p className="text-xs text-muted-foreground mb-3">
          Ocupan toda la pantalla, con el título encima. Si cargás más de una, la página las va pasando sola (cada 6 segundos), en este orden.
          El encuadre elige qué parte de la foto se prioriza cuando se recorta, sobre todo en el celular. Se guardan con &quot;Guardar cambios&quot;.
        </p>
        <div className="space-y-2" data-testid={LANDING_ADMIN.heroImagesList}>
          {heroImages.map((img, idx) => (
            <div key={`${img.url}-${idx}`} className="flex flex-wrap items-center gap-3 border border-border rounded-lg p-2" data-testid={`${LANDING_ADMIN.heroImageRowPrefix}${idx}`}>
              <img src={getFileUrl(img.url)} alt="" className="w-24 h-14 rounded object-cover shrink-0" />
              <span className="text-sm text-muted-foreground w-4">{idx + 1}</span>
              <Select value={img.position || "center"} onValueChange={(v) => setHeroImages((list) => list.map((x, i) => (i === idx ? { ...x, position: v } : x)))}>
                <SelectTrigger className="w-44 h-9" data-testid={`${LANDING_ADMIN.heroImagePositionPrefix}${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HERO_POSITIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1 ml-auto">
                <Button type="button" variant="ghost" size="icon" disabled={idx === 0} onClick={() => moveHeroImage(idx, -1)} aria-label="Subir" data-testid={`${LANDING_ADMIN.heroImageUpPrefix}${idx}`}>
                  <ArrowUp className="w-4 h-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" disabled={idx === heroImages.length - 1} onClick={() => moveHeroImage(idx, 1)} aria-label="Bajar" data-testid={`${LANDING_ADMIN.heroImageDownPrefix}${idx}`}>
                  <ArrowDown className="w-4 h-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" onClick={() => setHeroImages((list) => list.filter((_, i) => i !== idx))} aria-label="Quitar" data-testid={`${LANDING_ADMIN.heroImageRemovePrefix}${idx}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        {heroImages.length < MAX_HERO_IMAGES && (
          <div className="mt-3">
            <ImageUpload
              key={heroImages.length}
              currentUrl={null}
              onUploaded={(url) => setHeroImages((list) => [...list, { url, position: "center" }])}
              placeholderUrl={null}
              testId={LANDING_ADMIN.heroImageUpload}
              shape="square"
              maxWidth={2000}
              buttonLabel="Agregar foto"
            />
          </div>
        )}
      </div>
      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Título principal</Label>
        <Input value={form.hero_title || ""} onChange={set("hero_title")} placeholder="Belleza natural, con criterio médico" data-testid={LANDING_ADMIN.heroTitleInput} />
      </div>
      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Frase corta (subtítulo)</Label>
        <Input value={form.hero_subtitle || ""} onChange={set("hero_subtitle")} placeholder="Medicina estética premium en Houston" data-testid={LANDING_ADMIN.heroSubtitleInput} />
      </div>
      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Frase destacada de la portada</Label>
        <Textarea value={form.hero_quote || ""} onChange={set("hero_quote")} rows={2} placeholder="Se usa la frase por defecto si se deja vacío" data-testid={LANDING_ADMIN.heroQuoteInput} />
      </div>
      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Título de la presentación</Label>
        <Input value={form.intro_title || ""} onChange={set("intro_title")} placeholder="Nuestro enfoque" data-testid={LANDING_ADMIN.introTitleInput} />
      </div>
      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Presentación de la clínica (2-3 frases)</Label>
        <Textarea value={form.intro_text || ""} onChange={set("intro_text")} rows={3} data-testid={LANDING_ADMIN.introTextInput} />
      </div>
      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Horarios de atención</Label>
        <Textarea value={form.business_hours || ""} onChange={set("business_hours")} rows={2} placeholder={"Lunes a viernes: 9 a 18hs\nSábados: 9 a 13hs"} data-testid={LANDING_ADMIN.businessHoursInput} />
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <Switch checked={form.show_results_gallery} onCheckedChange={(v) => setForm((f) => ({ ...f, show_results_gallery: v }))} data-testid={LANDING_ADMIN.showGalleryToggle} />
        <span className="text-sm text-foreground">Mostrar galería de resultados (antes/después) en la landing</span>
      </label>

      <div className="border-t border-border pt-5 space-y-4">
        <p className="text-sm font-medium text-foreground">SEO / redes sociales</p>
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Título SEO</Label>
          <Input value={form.meta_title || ""} onChange={set("meta_title")} placeholder="Strani Just Because · Medicina Estética Houston" data-testid={LANDING_ADMIN.metaTitleInput} />
        </div>
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Descripción SEO</Label>
          <Textarea value={form.meta_description || ""} onChange={set("meta_description")} rows={2} data-testid={LANDING_ADMIN.metaDescriptionInput} />
        </div>
      </div>

      <div className="pt-1">
        <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={LANDING_ADMIN.saveContentButton}>
          {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </div>
    </div>
  );
}
